import { apiClient } from "./apiClient";

const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

interface ProfileCacheEntry {
  data?: ProfileResponse;
  expiresAt: number;
  promise?: Promise<ProfileResponse>;
}

const profileCache = new Map<string, ProfileCacheEntry>();

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

/**
 * One email can belong to several unrelated organisations, and the backend returns a
 * different tree per organisation — so the organisation has to be part of the cache key,
 * otherwise switching organisations serves the previous one's tree.
 */
function cacheKey(email: string, legalEntityId?: string | null) {
  return `${normalizeEmail(email)}::${legalEntityId ?? ""}`;
}

async function fetchProfile(email: string, legalEntityId?: string | null): Promise<ProfileResponse> {
  const res = await apiClient.get<ProfileResponse>("/profile", {
    params: legalEntityId ? { email, legalEntityId } : { email },
  });
  return res.data;
}

function setProfileCache(email: string, legalEntityId: string | null | undefined, data: ProfileResponse) {
  profileCache.set(cacheKey(email, legalEntityId), {
    data,
    expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
  });
}

/** Drops every organisation's entry for this email — callers mutate org-wide state. */
function clearProfileCache(email: string) {
  const prefix = `${normalizeEmail(email)}::`;
  for (const key of [...profileCache.keys()]) {
    if (key.startsWith(prefix)) profileCache.delete(key);
  }
}

function getCachedProfile(
  email: string,
  legalEntityId?: string | null,
  force = false,
): Promise<ProfileResponse> {
  const key = cacheKey(email, legalEntityId);
  const now = Date.now();
  const cached = profileCache.get(key);

  if (!force && cached?.data && cached.expiresAt > now) {
    return Promise.resolve(cached.data);
  }

  if (!force && cached?.promise) {
    return cached.promise;
  }

  const promise = fetchProfile(email, legalEntityId)
    .then((data) => {
      setProfileCache(email, legalEntityId, data);
      return data;
    })
    .catch((err) => {
      const latest = profileCache.get(key);
      if (latest?.promise === promise) {
        profileCache.delete(key);
      }
      throw err;
    });

  profileCache.set(key, {
    data: cached?.data,
    expiresAt: cached?.expiresAt ?? 0,
    promise,
  });

  return promise;
}

/** IMAP block returned per channel row on the profile hierarchy. */
export interface ChannelImapSettings {
  address?: string | null;
  port?: string | null;
  emailId?: string | null;
  appPassword?: string | null;
  enableSsl?: boolean | null;
}

/** SMTP block returned per channel row on the profile hierarchy. */
export interface ChannelSmtpSettings {
  address?: string | null;
  port?: string | null;
  emailId?: string | null;
  appPassword?: string | null;
  domain?: string | null;
  encryption?: string | null;
  opensslVerifyMode?: string | null;
  authentication?: string | null;
}

export interface BranchHierarchy {
  id: string;
  name: string;
  groupId: string;
  logoUrl?: string | null;
  sourceCrm?: string | null;
  agencyRef?: string | null;
  legalEntityId?: string | null;
  /** User-entered WhatsApp phone. Drives the input prefill in the UI. */
  whatsappPhone?: string | null;
  /** UI toggle — set true once a phone is saved. Drives Connect / Sync button visibility. */
  whatsappEnabled?: boolean | null;
  /** Provider-assigned id (e.g. Chatwoot inbox), only filled after onboarding. */
  externalChannelIds?: Record<string, string>;
  /**
   * Chatwoot account id this branch maps to. Lets the Inboxes deep-link switch
   * Chatwoot to the right account instead of the user's default one.
   */
  chatwootAccountId?: string | number | null;
  /** Channel-live flag, only flips true once provisioning completes. */
  channelActives?: Record<string, boolean>;
  channelIds?: Record<string, string>;
  /** Meta phone number ID — prefilled into the Communication Channels form. */
  whatsappPhoneNumberId?: string | null;
  /** Long-lived WhatsApp Business access token. */
  whatsappAccessToken?: string | null;
  /** Meta WhatsApp Business Account ID (WABA). */
  whatsappBusinessAccountId?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  /** True when this is the primary branch for its legal entity. */
  isPrimary?: boolean | null;
  /** True when the branch has been successfully synced from the CRM. */
  branchSynced?: boolean;
  whatsappTemplateSetupStatus?: string | null;
  whatsappTemplateSetupError?: string | null;
  /** Email channel address; prefills the Email Configuration form. */
  emailId?: string | null;
  /** UI toggle — mirrors {@code whatsappEnabled} for the email channel. */
  emailEnabled?: boolean | null;
  /** Friendly inbox name shown in the Email Configuration form. */
  inboxLabel?: string | null;
  imap?: ChannelImapSettings | null;
  smtp?: ChannelSmtpSettings | null;
}

export interface LegalEntityHierarchy {
  id: string;
  name: string;
  groupId: string;
  logoUrl?: string | null;
  sourceCrm?: string | null;
  agencyRef?: string | null;
  whatsappPhone?: string | null;
  whatsappEnabled?: boolean | null;
  externalChannelIds?: Record<string, string>;
  channelActives?: Record<string, boolean>;
  channelIds?: Record<string, string>;
  whatsappPhoneNumberId?: string | null;
  whatsappAccessToken?: string | null;
  whatsappBusinessAccountId?: string | null;
  whatsappTemplateSetupStatus?: string | null;
  whatsappTemplateSetupError?: string | null;
  /**
   * Chatwoot account id this legal entity maps to. Lets the Inboxes deep-link
   * switch Chatwoot to the right account instead of the user's default one.
   */
  chatwootAccountId?: string | number | null;
  primaryEmail?: string | null;
  phoneNumber?: string | null;
  /** Email channel address; prefills the Email Configuration form. */
  emailId?: string | null;
  /** UI toggle — mirrors {@code whatsappEnabled} for the email channel. */
  emailEnabled?: boolean | null;
  /** Friendly inbox name shown in the Email Configuration form. */
  inboxLabel?: string | null;
  imap?: ChannelImapSettings | null;
  smtp?: ChannelSmtpSettings | null;
  /**
   * Propco credentials. Propco authenticates with one account for the whole
   * group, so these live on the ROOT legal entity only — there is no per-branch
   * equivalent, unlike the ref-mapped CRMs.
   */
  username?: string | null;
  password?: string | null;
  branches: BranchHierarchy[];
  subEntities: LegalEntityHierarchy[];
}

export interface ProfileResponse {
  email: string;
  name: string | null;
  companyName: string | null;
  phoneNumber: string | null;
  groupName: string | null;
  groupId: string | null;
  profileCompleted: boolean;
  userType: string | null;
  userId: string | null;
  legalEntities: LegalEntityHierarchy[];
}

/**
 * Editable Legal Entity profile — the company-level details a Branch Manager
 * fills in on /profile/setup. These persist on the legal_entities table, NOT
 * on the user record.
 */
export interface LegalEntityProfile {
  id: string;
  groupId: string | null;
  parentGroupId: string | null;
  displayName: string | null;
  legalName: string | null;
  tradingName: string | null;
  companyNumber: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  website: string | null;
  registeredStreet: string | null;
  registeredTown: string | null;
  registeredCounty: string | null;
  registeredPostcode: string | null;
  registeredCountryCode: string | null;
  logoUrl: string | null;
  /** Non-fatal warning from the asynchronous/external Meta logo synchronization. */
  metaLogoSyncWarning?: string | null;
}

export interface BranchProfile {
  id: string;
  name: string | null;
  email: string | null;
  workPhone: string | null;
  website: string | null;
  logoUrl: string | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  linkedinUrl: string | null;
  /** Non-fatal warning from the asynchronous/external Meta logo synchronization. */
  metaLogoSyncWarning?: string | null;
}

export interface LogoSyncResult {
  status: "SYNCED" | "NO_LOGO" | "NO_CHANNEL" | "FAILED";
  warning: string | null;
}

export interface CreateLegalEntityPayload {
  companyName: string;
  parentLegalEntityId?: string | null;
  email?: string | null;
  password?: string | null;
  logoUrl?: string | null;
  phoneNumber?: string | null;
}

export interface CreateBranchPayload {
  name: string;
  legalEntityId: string;
  logoUrl?: string | null;
  phoneNumber?: string | null;
}

export interface UpdateLegalEntityHierarchyPayload {
  name?: string;
  email?: string;
  logoUrl?: string | null;
  phoneNumber?: string | null;
}

export interface UpdateBranchHierarchyPayload {
  name?: string;
  email?: string;
  logoUrl?: string | null;
  phoneNumber?: string | null;
}

/**
 * UserTypes the UI is allowed to invite via "Add Team Member". Matches the
 * backend allow-list — NEGOTIATOR is intentionally excluded.
 */
export type TeamMemberUserType = "SUPPORT_USER" | "BRANCH_MANAGER";

export interface AddTeamMemberPayload {
  email: string;
  name: string;
  password: string;
  legalEntityId: string;
  branchId?: string;
  userType: TeamMemberUserType;
}

export const profileService = {
  /**
   * @param legalEntityId the selected organisation. Any legal entity in the tree works —
   *   the backend resolves it up to its root. Omit before an organisation is chosen.
   */
  get: async (email: string, legalEntityId?: string | null): Promise<ProfileResponse> => {
    return getCachedProfile(email, legalEntityId);
  },

  refresh: async (email: string, legalEntityId?: string | null): Promise<ProfileResponse> => {
    return getCachedProfile(email, legalEntityId, true);
  },

  invalidate: (email: string) => {
    clearProfileCache(email);
  },

  complete: async (email: string) => {
    const res = await apiClient.post("/profile/complete", null, { params: { email } });
    clearProfileCache(email);
    return res;
  },

  updatePhone: async (email: string, phone: string) => {
    const res = await apiClient.patch("/profile/phone", null, { params: { email, phone } });
    clearProfileCache(email);
    return res;
  },

  updateCompany: async (email: string, company: string) => {
    const res = await apiClient.patch("/profile/company", null, { params: { email, company } });
    clearProfileCache(email);
    return res;
  },

  /** Returns the Branch Manager's primary Legal Entity (company-level details). */
  getLegalEntity: async (email: string): Promise<LegalEntityProfile> => {
    const res = await apiClient.get<LegalEntityProfile>("/profile/legal-entity", {
      params: { email },
    });
    return res.data;
  },

  /** Partial update — null/blank fields are left untouched server-side. */
  updateLegalEntity: async (
    email: string,
    updates: Partial<LegalEntityProfile>,
  ): Promise<LegalEntityProfile> => {
    const res = await apiClient.patch<LegalEntityProfile>("/profile/legal-entity", updates, {
      params: { email },
    });
    clearProfileCache(email);
    return res.data;
  },

  /** On-demand (re)push of the current logo to WhatsApp for the primary Legal Entity. */
  syncLegalEntityLogo: async (email: string): Promise<LogoSyncResult> => {
    const res = await apiClient.post<LogoSyncResult>("/profile/legal-entity/sync-logo", null, {
      params: { email },
    });
    return res.data;
  },

  /**
   * Provision a sub-Legal-Entity. Body mirrors the signup form: a new user
   * (newUser email + password) is created in Keycloak and emailed a
   * verification link; backend creates the matching sub-group + LE.
   */
  createLegalEntity: async (callerEmail: string, payload: CreateLegalEntityPayload) => {
    const res = await apiClient.post("/profile/legal-entities", payload, {
      params: { email: callerEmail },
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  /** Same shape as createLegalEntity but the new sub-group sits under the chosen LE. */
  createBranch: async (callerEmail: string, payload: CreateBranchPayload) => {
    const res = await apiClient.post("/profile/branches", payload, {
      params: { email: callerEmail },
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  /** Returns the user's Branch details. */
  getBranch: async (email: string): Promise<BranchProfile> => {
    const res = await apiClient.get<BranchProfile>("/profile/branch", {
      params: { email },
    });
    return res.data;
  },

  /** Update branch details. */
  updateBranch: async (email: string, updates: Partial<BranchProfile>): Promise<BranchProfile> => {
    const res = await apiClient.patch<BranchProfile>("/profile/branch", updates, {
      params: { email },
    });
    clearProfileCache(email);
    return res.data;
  },

  /** On-demand (re)push of the current logo to WhatsApp for the user's branch — falls
   * back to the parent Legal Entity's logo when the branch has none of its own. */
  syncBranchLogo: async (email: string): Promise<LogoSyncResult> => {
    const res = await apiClient.post<LogoSyncResult>("/profile/branch/sync-logo", null, {
      params: { email },
    });
    return res.data;
  },

  /**
   * Invite a team member into one of the caller's Legal Entities (and
   * optionally a specific Branch). Backend creates the Keycloak user, attaches
   * them to the right group, creates an Atlas user row, and sends a
   * verification email.
   */
  addTeamMember: async (callerEmail: string, payload: AddTeamMemberPayload) => {
    const res = await apiClient.post("/profile/team-members", payload, {
      params: { email: callerEmail },
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  moveEntities: async (callerEmail: string, type: "LEGAL_ENTITY" | "BRANCH", ids: string[], targetParentId: string) => {
    const res = await apiClient.post("/profile/hierarchy/move", { type, ids, targetParentId }, {
      params: { email: callerEmail }
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  deleteEntity: async (callerEmail: string, type: "LEGAL_ENTITY" | "BRANCH", id: string) => {
    const res = await apiClient.delete(`/profile/hierarchy/${type}/${id}`, {
      params: { email: callerEmail }
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  updateEntity: async (
    callerEmail: string,
    type: "LEGAL_ENTITY" | "BRANCH",
    id: string,
    updates: UpdateLegalEntityHierarchyPayload | UpdateBranchHierarchyPayload,
    legalEntityId?: string | null,
  ) => {
    const res = await apiClient.patch(`/profile/hierarchy/${type}/${id}`, updates, {
      params: legalEntityId
        ? { email: callerEmail, legalEntityId }
        : { email: callerEmail },
    });
    clearProfileCache(callerEmail);
    return res.data;
  },

  /** On-demand (re)push of the current logo to WhatsApp for an arbitrary entity in the
   * hierarchy — unlike syncLegalEntityLogo/syncBranchLogo, not limited to the caller's own. */
  syncEntityLogo: async (
    callerEmail: string,
    type: "LEGAL_ENTITY" | "BRANCH",
    id: string,
  ): Promise<LogoSyncResult> => {
    const res = await apiClient.post<LogoSyncResult>(`/profile/hierarchy/${type}/${id}/sync-logo`, null, {
      params: { email: callerEmail },
    });
    return res.data;
  },
};
